import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const initialState = {
  title: '',
  address1: '',
  town: '',
  county: '',
  postcode: '',
  type: '',
  status: '',
  description: '',
  purchasePrice: '',
  rentalCost: '',
  image: null,
};

const authHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const propertyTypes = [
  { value: '', label: 'Select type' },
  { value: 'house', label: 'House' },
  { value: 'flat', label: 'Flat' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'hmo', label: 'HMO' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed-use', label: 'Mixed Use' },
];

const propertyStatuses = [
  { value: '', label: 'Select status' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'occupied', label: 'Occupied' },
];

const EditProperty = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { run: runSubmission, isPending } = useSubmissionLock();

  // Fetch existing property
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axiosInstance.get(`/api/dashboard/properties/${id}`, {
          withCredentials: true,
          headers: authHeaders(),
        });

        const data = res.data;

        setForm({
          ...initialState,
          ...data,
          description: data.description || '',
          purchasePrice: data.purchasePrice || '',
          rentalCost: data.rentalCost || '',
          image: null, // Don't prepopulate the image input
        });

        if (data.imageUrl) {
          setImagePreview(data.imageUrl);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchProperty();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      const file = files?.[0] || null;

      setForm((prev) => ({
        ...prev,
        image: file,
      }));

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
      }

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending(`property:${id}:save`)) return;

    await runSubmission(`property:${id}:save`, async () => {
      setSaving(true);

      try {
        const formData = new FormData();

        formData.append('title', form.title || '');
        formData.append('address1', form.address1 || '');
        formData.append('town', form.town || '');
        formData.append('county', form.county || '');
        formData.append('postcode', form.postcode || '');
        formData.append('type', form.type || '');
        formData.append('status', form.status || '');
        formData.append('description', form.description || '');
        formData.append('purchasePrice', form.purchasePrice || '');
        formData.append('rentalCost', form.rentalCost || '');

        if (form.image instanceof File) {
          formData.append('image', form.image, form.image.name);
        }

        const res = await axiosInstance.put(`/api/dashboard/properties/${id}`, formData, {
          withCredentials: true,
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        });

        console.log('Updated property:', res.data);

        navigate(`/property/${id}`);
      } catch (err) {
        console.error('Update failed:', err);
      } finally {
        setSaving(false);
      }
    });
  };

  const handleDelete = async () => {
    if (saving || isPending(`property:${id}:delete`)) return;

    await runSubmission(`property:${id}:delete`, async () => {
      if (!window.confirm('Are you sure you want to delete this property?')) return;

      setSaving(true);

      try {
        await axiosInstance.delete(`/api/dashboard/properties/${id}`, {
          withCredentials: true,
          headers: authHeaders(),
        });

        navigate('/dashboard');
      } catch (err) {
        console.error('Delete failed:', err);
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto mt-10 bg-white/10 rounded-xl border border-white/10 p-8 backdrop-blur-md text-white">
      <button
        className="flex items-center gap-2 mb-6 text-white hover:text-[#AF9B8A] transition"
        onClick={() => navigate(-1)}
        type="button"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>

      <h2 className="text-2xl font-bold mb-6">Edit Property</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block mb-1 font-semibold">Title</label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block mb-1 font-semibold">Address</label>
          <div className="flex gap-4">
            <input
              name="address1"
              value={form.address1}
              onChange={handleChange}
              placeholder="Street"
              required
              className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
            <input
              name="town"
              value={form.town}
              onChange={handleChange}
              placeholder="Town"
              required
              className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
          </div>

          <div className="flex gap-4 mt-4">
            <input
              name="county"
              value={form.county}
              onChange={handleChange}
              placeholder="County"
              required
              className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
            <input
              name="postcode"
              value={form.postcode}
              onChange={handleChange}
              placeholder="Postcode"
              required
              className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 relative">
            <label className="block mb-1 font-semibold">Type</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              required
              className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
            >
              {propertyTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
          </div>

          <div className="flex-1 relative">
            <label className="block mb-1 font-semibold">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              required
              className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
            >
              {propertyStatuses.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block mb-1 font-semibold">Purchase Price (£)</label>
            <input
              type="number"
              name="purchasePrice"
              value={form.purchasePrice}
              onChange={handleChange}
              placeholder="e.g. 250000"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
          </div>

          <div className="flex-1">
            <label className="block mb-1 font-semibold">Rental Cost (£/month)</label>
            <input
              type="number"
              name="rentalCost"
              value={form.rentalCost}
              onChange={handleChange}
              placeholder="e.g. 1600"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
          </div>
        </div>

        <div>
          <label className="block mb-1 font-semibold">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block mb-1 font-semibold">Upload Image</label>
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleChange}
            className="block w-full text-white"
          />

          {imagePreview && (
            <img src={imagePreview} alt="Preview" className="mt-2 rounded-lg max-h-40" />
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 bg-[#AF9B8A] rounded-xl text-white font-semibold hover:bg-[#9b897a] transition"
        >
          {isPending(`property:${id}:save`) ? 'Saving...' : 'Save Property'}
        </button>

        <button
          type="button"
          onClick={handleDelete}
          disabled={saving}
          className="w-full py-3 mt-2 bg-red-600 rounded-xl text-white font-semibold hover:bg-red-700 transition"
        >
          {isPending(`property:${id}:delete`) ? 'Deleting...' : 'Delete Property'}
        </button>
      </form>
    </div>
  );
};

export default EditProperty;
